import creativeApi from "@api/creatives" //eslint-disable-line
import authApi from "@api/auth" //eslint-disable-line
import Vue from 'vue'
import { errorPromise } from '@util/ErrorHandling.js'

export default {
    namespaced: true,
    state: {
        loading: {
            creative: false,
            creativeList: false,
            creativeSave: false,
            passthroughFieldsList: false,
        },
        errors: {
            creative: null,
            creativeList: null,
            creativeSave: null,
            passthroughFieldsListGet: null,
        },
        creative: [],
        creativeHistory: [],
        creativeList: [],
        passthroughFieldsList: [],
    },
    getters: {
        creative: ({ creative }) => creative,
        creativeHistory: state => state.creativeHistory,
        creativeList: state => state.creativeList,
        creativeByUuid: state => uuid => state.creativeList.find(creative => creative.uuid === uuid),
        creativesByProductUuid: state => productUuid => state.creativeList.filter(
            creative => (creative.product && creative.product.uuid === productUuid)
                || creative.product_uuid === productUuid,
        ),
        getLoadingState: state => name => state.loading[name],
        getErrorState: state => name => state.errors[name],
        passthroughFieldsList: state => state.passthroughFieldsList,
    },
    mutations: {
        setError(state, error) {
            Vue.set(state.errors, error.name, error.value)
        },
        setLoadingState(state, loading) {
            Vue.set(state.loading, loading.name, loading.value)
        },
        setCreative(state, data) {
            const creative = { ...data, product_uuid: data.product.uuid }
            Vue.set(state, 'creative', creative)
        },
        setCreativeHistory(state, history) {
            Vue.set(state, 'creativeHistory', history)
        },
        setCreativeList(state, creatives) {
            Vue.set(state, 'creativeList', creatives)
        },
        deleteCreative(state, uuid) {
            const creativeIndex = state.creativeList.findIndex(creative => creative.uuid === uuid)
            state.creativeList.splice(creativeIndex, 1)
        },
        addCreative(state, creative) {
            state.creativeList.push(creative)
        },
        updateCreativeProperty(state, payload) {
            Vue.set(state.creative, payload.property, payload.value)
        },
        setPassthroughFieldsList(state, passthroughFieldsList) {
            Vue.set(state, 'passthroughFieldsList', passthroughFieldsList)
        },
    },
    actions: {
        getCreative({ commit }, uuid) {
            commit('setLoadingState', { name: 'creative', value: true })

            return creativeApi
                .getCreative(uuid)
                .then(response => {
                    const { data } = response.data
                    commit('setCreative', data)
                })
                .catch(() => {
                    commit('setError', { name: 'creative', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creative', value: false })
                })
        },
        getCreativeHistory({ commit }, uuid) {
            commit('setCreativeHistory', [])
            commit('setLoadingState', { name: 'creativeHistory', value: true })

            return creativeApi
                .getCreativeHistory(uuid)
                .then(response => {
                    const history = response.data.data
                    commit('setCreativeHistory', history)
                })
                .catch(() => {
                    commit('setError', { name: 'creativetHistory', value: true })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creativeHistory', value: false })
                })
        },
        setCreativeList({ commit }) {
            commit('setLoadingState', { name: 'creativeList', value: true })

            creativeApi
                .getCreatives()
                .then(response => {
                    const { data } = response.data

                    commit('setCreativeList', data)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'creativeList',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creativeList', value: false })
                })
        },
        addCreative({ commit }, creativePayload) {
            commit('setLoadingState', { name: 'creativeSave', value: true })
            commit('setError', { name: 'creativeSave', value: null })

            creativePayload.target_url = creativePayload.target_url.replace(/[(&|?)+]+$/gm, '')

            creativePayload.passthrough_fields = creativePayload.passthrough_fields.map(field => ({
                ...field,
                param_name: field.param_name || field.value, // If param_name is null or empty, use value
            }));

            // Empty string fails the nullable regex rule; send null so it falls back to "cuuid"
            creativePayload.cuuid_param_name = creativePayload.cuuid_param_name || null

            return creativeApi
                .addCreative(creativePayload)
                .then(response => {
                    const creative = {
                        ...response.data.data,
                        product_uuid: creativePayload.product_uuid,
                    }

                    commit('addCreative', creative)
                })
                .catch(errors => {
                    commit('setError', { name: 'creativeSave', value: true })

                    return errorPromise(errors, 'There was an error saving the ad')
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creativeSave', value: false })
                })
        },
        updateCreative({ commit, state }) {
            commit('setLoadingState', { name: 'creativeSave', value: true })
            commit('setError', { name: 'creativeSave', value: null })

            state.creative.target_url = state.creative.target_url.replace(/[(&|?)+]+$/gm, '')

            state.creative.passthrough_fields = state.creative.passthrough_fields.map(field => ({
                ...field,
                param_name: field.param_name || field.value, // If param_name is null or empty, use value
            }));

            // Empty string fails the nullable regex rule; send null so it falls back to "cuuid"
            state.creative.cuuid_param_name = state.creative.cuuid_param_name || null

            return creativeApi
                .updateCreative(state.creative.uuid, state.creative)
                .then(resp => resp.data.data)
                .catch(errors => {
                    commit('setError', { name: 'creativeSave', value: true })

                    return errorPromise(errors, 'There was an error updating the ad')
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creativeSave', value: false })
                })
        },
        clearError({ commit }, name) {
            commit('setError', {
                name,
                value: null,
            })
        },
        updateCreativeProperty({ commit }, payload) {
            commit('updateCreativeProperty', payload)
        },
        initializeCreativeManager({ dispatch }) {
            dispatch('setCreativeList')
        },
        deleteCreative({ commit }, uuid) {
            commit('setLoadingState', { name: 'creativeDelete', value: true })

            return creativeApi.deleteCreative(uuid)
                .then(() => {
                    commit('deleteCreative', uuid)
                }).catch(() => {
                    commit('setError', {
                        name: 'creativeDelete',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'creativeDelete', value: false })
                })
        },
        getPassthroughFieldsList({ commit }) {
            commit('setLoadingState', { name: 'passthroughFieldsList', value: true })
            return creativeApi.getPassthroughFieldsList()
                .then(data => {
                    const passthroughFieldsList = data.data.data
                    commit('setPassthroughFieldsList', passthroughFieldsList)
                })
                .catch(() => {
                    commit('setError', {
                        name: 'passthroughFieldsListGet',
                        value: true,
                    })
                })
                .finally(() => {
                    commit('setLoadingState', { name: 'passthroughFieldsList', value: false })
                })
        },
    },
}
